import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Badge,
  Box,
  Typography,
  useTheme,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { FraudFlagDto } from '@src/data/api/fraud/fraud-api'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FraudFlagCard } from './FraudFlagCard'

interface FraudFlagsGridProps {
  flagsByCategory: Record<string, FraudFlagDto[]>
  financingProgramId: string
  creditApplicationId: string
  indicatorType: string
  mode: 'category' | 'active'
  expandAllByDefault?: boolean
}

export const FraudFlagsGrid: React.FC<FraudFlagsGridProps> = ({ flagsByCategory, financingProgramId, creditApplicationId, indicatorType, mode, expandAllByDefault }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const [expandedCategories, setExpandedCategories] = useState<string[]>(
    expandAllByDefault ? Object.keys(flagsByCategory) : [], // Toutes les sections fermées par défaut sauf si expandAllByDefault est true
  )

  const handleAccordionChange = (category: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCategories((prev) => (isExpanded ? [...prev, category] : prev.filter((cat) => cat !== category)))
  }

  const getCategoryColor = (activeCount: number): { bg: string; indicator: string } => {
    if (activeCount === 0) {
      return {
        bg: alpha(theme.palette.success.main, 0.25),
        indicator: theme.palette.success.main,
      }
    } else if (activeCount <= 2) {
      return {
        bg: alpha(theme.palette.warning.main, 0.25),
        indicator: theme.palette.warning.main,
      }
    } else {
      return {
        bg: alpha(theme.palette.error.main, 0.25),
        indicator: theme.palette.error.main,
      }
    }
  }

  if (Object.keys(flagsByCategory).length === 0) {
    return <Alert severity="info">{mode === 'active' ? t('fraudFlags.noActiveFlags') : t('fraudFlags.noFlags')}</Alert>
  }

  return (
    <Box>
      {Object.entries(flagsByCategory).map(([category, flags]) => {
        const activeCount = flags.filter((f) => f.value).length
        const colors = getCategoryColor(activeCount)

        return (
          <Accordion
            key={category}
            expanded={expandedCategories.includes(category)}
            onChange={handleAccordionChange(category)}
            sx={{
              mb: 2,
              backgroundColor: colors.bg,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: alpha(theme.palette.action.hover, 0.04),
                '& .MuiAccordionSummary-content': {
                  color: 'text.primary',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box
                  sx={{
                    width: 4,
                    height: 24,
                    bgcolor: colors.indicator,
                    mr: 2,
                    borderRadius: '2px',
                  }}
                />
                <Typography sx={{ flexGrow: 1, fontWeight: 500 }}>
                  {t(`fraudFlags.categories.${category}`, { defaultValue: category })}
                </Typography>
                <Badge badgeContent={activeCount} color="error" sx={{ mr: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {flags.length} {t('fraudFlags.flags')}
                  </Typography>
                </Badge>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: theme.palette.background.paper }}>
              <Grid container spacing={2}>
                {flags.map((flag) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={flag.key}>
                    <FraudFlagCard
                      flag={flag}
                      indicatorType={indicatorType}
                      financingProgramId={financingProgramId}
                      creditApplicationId={creditApplicationId}
                      currentLanguage={i18n.language}
                      displayCategory={mode === 'active'}
                    />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}
